import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  Link,
  HStack,
  Icon,
  Flex,
  Divider,
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import type { HeroSlide, FeaturedCarousel } from "../types";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FaInstagram, FaTiktok } from "react-icons/fa";
import {
  HiOutlineShieldCheck,
  HiOutlineWrenchScrewdriver,
  HiOutlineCamera,
  HiOutlineMapPin,
} from "react-icons/hi2";
import { motion, useInView } from "framer-motion";
import { useCategories } from "../hooks/useCategories";
import { useProductsByIds } from "../hooks/useProducts";
import HeroCarousel from "../components/HeroCarousel";
import CategoryIconGrid from "../components/CategoryIconGrid";
import ProductCarousel from "../components/ProductCarousel";

// ─── Leaflet icon fix ─────────────────────────────────────────────────────────
interface IconDefault extends L.Icon.Default {
  _getIconUrl?: string;
}
delete (L.Icon.Default.prototype as IconDefault)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// ─── Motion helpers ───────────────────────────────────────────────────────────
const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const RevealSection = ({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px 0px" });
  return (
    <MotionBox
      ref={ref}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      transition={{ delay } as any}
    >
      {children}
    </MotionBox>
  );
};

// ─── Repair section ──────────────────────────────────────────────────────────
const RepairSection = () => {
  const features = [
    {
      icon: "💧",
      title: "Sensor cleaning",
      desc: "Professional wet and dry cleaning to remove dust and debris from your imaging sensor.",
    },
    {
      icon: "⚙️",
      title: "Shutter and mechanism repair",
      desc: "Full mechanical servicing and replacement calibrated to manufacturer specifications.",
    },
    {
      icon: "🔍",
      title: "Lens calibration and fungus removal",
      desc: "AF fine-tuning and optical element treatment to restore full clarity.",
    },
  ];

  return (
    <Box
      as="section"
      bg="rs.imageWell"
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor="rs.border"
      py={16}
      id="repair-and-services"
    >
      <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
        <RevealSection>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={16} alignItems="center">
            <Box>
              <Text
                fontSize="10.5px"
                fontWeight="500"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="rs.red"
                mb="7px"
              >
                Expert service
              </Text>
              <Heading
                fontFamily="'DM Serif Display', serif"
                fontSize={{ base: "26px", md: "32px" }}
                fontWeight="400"
                color="rs.text"
                lineHeight="1.2"
                mb={4}
              >
                Repair and servicing
                <br />
                <Box as="em" fontStyle="italic" color="rs.red">
                  by RED Shutter
                </Box>
              </Heading>
              <Text fontSize="13.5px" color="rs.textMuted" lineHeight="1.75" mb={0} maxW="400px">
                Expert care for every camera and lens brand. All work is backed by a 90-day
                service warranty.
              </Text>
              <VStack align="stretch" spacing={0} mt={6}>
                {features.map((f, i) => (
                  <Flex
                    key={i}
                    gap={4}
                    py={5}
                    borderBottom="1px solid"
                    borderColor="rs.border"
                    _first={{ borderTop: "1px solid", borderTopColor: "rs.border" }}
                    align="flex-start"
                  >
                    <Flex
                      w="36px"
                      h="36px"
                      borderRadius="6px"
                      bg="rs.redAlpha"
                      border="1px solid"
                      borderColor="rs.redBorder"
                      align="center"
                      justify="center"
                      fontSize="16px"
                      flexShrink={0}
                    >
                      {f.icon}
                    </Flex>
                    <Box>
                      <Text fontSize="13.5px" fontWeight="500" color="rs.text" mb="3px">
                        {f.title}
                      </Text>
                      <Text fontSize="12px" color="rs.textMuted" lineHeight="1.65">
                        {f.desc}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </VStack>
            </Box>

            <Box bg="rs.bg" border="1px solid" borderColor="rs.borderStrong" borderRadius="10px" p={8}>
              <Flex
                w="44px"
                h="44px"
                borderRadius="10px"
                bg="rs.redAlpha"
                border="1px solid"
                borderColor="rs.redBorder"
                align="center"
                justify="center"
                fontSize="20px"
                mb={5}
              >
                🔧
              </Flex>
              <Heading fontFamily="'DM Serif Display', serif" fontSize="22px" fontWeight="400" color="rs.text" mb={3}>
                RED Shutter Service Center
              </Heading>
              <Text fontSize="13px" color="rs.textMuted" lineHeight="1.75" mb={7}>
                Authorised repair for all major camera and lens brands. Every job is backed by a
                90-day service warranty.
              </Text>
              <SimpleGrid columns={2} gap="1px" bg="rs.border" borderRadius="8px" overflow="hidden" mb={6}>
                {[
                  { val: "48h", lbl: "Avg. turnaround" },
                  { val: "90d", lbl: "Service warranty" },
                ].map(({ val, lbl }) => (
                  <Flex key={lbl} direction="column" align="center" justify="center" bg="rs.surface" py={5}>
                    <Text fontFamily="'DM Serif Display', serif" fontSize="26px" color="rs.red">
                      {val}
                    </Text>
                    <Text fontSize="10.5px" color="rs.textDim" mt="3px" letterSpacing="0.05em">
                      {lbl}
                    </Text>
                  </Flex>
                ))}
              </SimpleGrid>
              <Box
                as="a"
                href="#contact"
                display="block"
                textAlign="center"
                bg="rs.red"
                color="white"
                fontSize="12.5px"
                fontWeight="500"
                letterSpacing="0.04em"
                py={3}
                borderRadius="6px"
                transition="background 0.15s"
                _hover={{ bg: "rs.redHover", textDecoration: "none" }}
              >
                Book a service
              </Box>
            </Box>
          </SimpleGrid>
        </RevealSection>
      </Container>
    </Box>
  );
};

// ─── Home ─────────────────────────────────────────────────────────────────────
const Home = () => {
  const { categories, isLoading: categoriesLoading } = useCategories();
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>([]);
  const [carousels, setCarousels] = useState<FeaturedCarousel[]>([]);

  const storeLocation = { lat: 7.2942, lng: 80.6378 };

  useEffect(() => {
    const fetchHomepageContent = async () => {
      const slidesSnap = await getDocs(
        query(collection(db, "heroSlides"), orderBy("order", "asc")),
      );
      setHeroSlides(
        (slidesSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as HeroSlide[]).filter(
          (s) => s.active !== false,
        ),
      );

      const carouselsSnap = await getDocs(
        query(collection(db, "featuredCarousels"), orderBy("order", "asc")),
      );
      const allCarousels = carouselsSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as FeaturedCarousel[];
      setCarousels(
        allCarousels.filter((c) => c.active !== false && !c.categorySlug),
      );
    };
    fetchHomepageContent();
  }, []);

  return (
    <>
      <Helmet>
        <title>Red Shutter Camera Store | Professional Photography Equipment in Sri Lanka</title>
        <meta
          name="description"
          content="Discover our wide range of professional cameras, lenses, accessories, lighting equipment, and tripods. Your trusted photography equipment store in Sri Lanka."
        />
        <meta
          name="keywords"
          content="camera store, photography equipment, cameras, lenses, photography accessories, lighting equipment, tripods, Sri Lanka"
        />
        <link rel="canonical" href="https://redshutter.com" />
      </Helmet>

      <Box as="main" bg="rs.bg" minH="100vh" width="100%" overflow="hidden">
        <HeroCarousel slides={heroSlides} />

        {/* ── Trust bar ──────────────────────────────────────────────────────── */}
        <RevealSection>
          <Box bg="rs.bgAlt" borderBottom="1px solid" borderColor="rs.border">
            <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={0}>
                {[
                  { icon: HiOutlineShieldCheck, label: "Genuine products", sub: "Authorised dealer" },
                  { icon: HiOutlineWrenchScrewdriver, label: "In-house repair", sub: "48h turnaround" },
                  { icon: HiOutlineCamera, label: "Rentals from LKR 1,500", sub: "Daily and weekly rates" },
                  { icon: HiOutlineMapPin, label: "City Plaza, Kandy", sub: "Walk-in welcome" },
                ].map(({ icon, label, sub }, i, arr) => (
                  <Flex
                    key={label}
                    align="center"
                    gap={4}
                    py={6}
                    px={{ base: 0, md: 6 }}
                    borderRight={{
                      base: "none",
                      md: i === arr.length - 1 ? "none" : "1px solid",
                    }}
                    borderBottom={{
                      base: i === arr.length - 1 ? "none" : "1px solid",
                      md: "none",
                    }}
                    borderColor="rs.border"
                  >
                    <Flex
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg="rs.surface"
                      border="1px solid"
                      borderColor="rs.redBorder"
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <Icon as={icon} boxSize="19px" color="rs.red" />
                    </Flex>
                    <Box>
                      <Text fontSize="13px" fontWeight="600" color="rs.text" lineHeight="1.3">
                        {label}
                      </Text>
                      <Text fontSize="11.5px" color="rs.textDim" mt="2px">
                        {sub}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </SimpleGrid>
            </Container>
          </Box>
        </RevealSection>

        {/* ── Category icon grid ────────────────────────────────────────────── */}
        {!categoriesLoading && <CategoryIconGrid categories={categories} />}

        {/* ── Featured carousels ────────────────────────────────────────────── */}
        {carousels.map((carousel) => (
          <FeaturedCarouselRow key={carousel.id} carousel={carousel} />
        ))}

        {/* ── Repair & Services ─────────────────────────────────────────────── */}
        {categories.some((c) => c.slug === "repair-and-services") && <RepairSection />}

        {/* ── Contact ──────────────────────────────────────────────────────── */}
        <Box as="section" id="contact" bg="rs.bg" py={16} borderTop="1px solid" borderColor="rs.border">
          <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
            <RevealSection>
              <Text
                fontSize="10.5px"
                fontWeight="500"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="rs.red"
                mb="7px"
              >
                Get in touch
              </Text>
              <Heading fontFamily="'DM Serif Display', serif" fontSize={{ base: "26px", md: "30px" }} fontWeight="400" color="rs.text" mb={10}>
                Contact us
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Flex direction="column" gap={5} p={8} bg="rs.surface" borderRadius="10px" border="1px solid" borderColor="rs.border">
                  <Box>
                    <Text fontSize="10px" fontWeight="600" letterSpacing="0.12em" textTransform="uppercase" color="rs.textDim" mb={1}>
                      Address
                    </Text>
                    <Text fontSize="14px" color="rs.textMuted">
                      71,1, 1st Floor, City Plaza Shopping Complex, 20000
                    </Text>
                  </Box>
                  <Divider borderColor="rs.border" />
                  <Box>
                    <Text fontSize="10px" fontWeight="600" letterSpacing="0.12em" textTransform="uppercase" color="rs.textDim" mb={1}>
                      Phone
                    </Text>
                    <Link href="tel:+94765420007" fontSize="14px" color="rs.red" _hover={{ color: "rs.redLinkHover" }}>
                      (76) 542 0007
                    </Link>
                  </Box>
                  <Divider borderColor="rs.border" />
                  <Box>
                    <Text fontSize="10px" fontWeight="600" letterSpacing="0.12em" textTransform="uppercase" color="rs.textDim" mb={1}>
                      Email
                    </Text>
                    <Link href="mailto:redshuttermedia16@gmail.com" fontSize="14px" color="rs.red" _hover={{ color: "rs.redLinkHover" }}>
                      redshuttermedia16@gmail.com
                    </Link>
                  </Box>
                  <Divider borderColor="rs.border" />
                  <HStack spacing={4} pt={1}>
                    {[
                      { href: "https://www.instagram.com/redshuttercamerastore/", icon: FaInstagram, label: "Instagram" },
                      { href: "https://www.tiktok.com/@redshuttercameras", icon: FaTiktok, label: "TikTok" },
                    ].map(({ href, icon, label }) => (
                      <Link
                        key={label}
                        href={href}
                        isExternal
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="36px"
                        h="36px"
                        borderRadius="8px"
                        bg="rs.redAlpha"
                        border="1px solid"
                        borderColor="rs.redBorder"
                        color="rs.red"
                        transition="all 0.15s"
                        _hover={{ bg: "rs.red", color: "white", textDecoration: "none" }}
                        aria-label={label}
                      >
                        <Icon as={icon} boxSize={4} />
                      </Link>
                    ))}
                  </HStack>
                </Flex>

                <Box h="400px" position="relative" borderRadius="10px" overflow="hidden" border="1px solid" borderColor="rs.border">
                  <MapContainer center={[storeLocation.lat, storeLocation.lng]} zoom={13} style={{ height: "100%", width: "100%" }}>
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <Marker position={[storeLocation.lat, storeLocation.lng]}>
                      <Popup>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold">Red Shutter Camera Store</Text>
                          <Text>71,1, 1st Floor, City Plaza Shopping Complex</Text>
                          <Link href="tel:+94765420007" color="blue.500">
                            (76) 542 0007
                          </Link>
                        </VStack>
                      </Popup>
                    </Marker>
                  </MapContainer>
                </Box>
              </SimpleGrid>
            </RevealSection>
          </Container>
        </Box>
      </Box>
    </>
  );
};

const FeaturedCarouselRow = ({ carousel }: { carousel: FeaturedCarousel }) => {
  const { products } = useProductsByIds(carousel.productIds);
  return <ProductCarousel title={carousel.title} products={products} />;
};

export default Home;
